import type { Room, Timeslot, Session, ScheduleItem } from '../types';
import { SessionQuickBookmark, SessionStatusTags } from './BoardSessionCardExtras';

export interface ScheduledSessionEntry {
  session: Session;
  item: ScheduleItem;
  room: Room;
  timeslot: Timeslot;
}

interface BoardListViewProps {
  rooms: Room[];
  timeslots: Timeslot[];
  sessions: Session[];
  scheduleItems: ScheduleItem[];
  votedSessionIds?: Set<string>;
  bookmarkedSessionIds?: Set<string>;
  /** When set, only show sessions in filterSessionIds. */
  interestFilter?: boolean;
  filterSessionIds?: Set<string>;
  onSessionClick?: (sessionId: string) => void;
  canBookmark?: boolean;
  onToggleBookmark?: (sessionId: string) => void;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateRange(startTs: string, endTs: string, spanSlots: number, timeslots: Timeslot[]): string {
  const start = formatTime(startTs);
  const startIdx = timeslots.find((t) => t.startTs === startTs)?.indexInDay;
  let endTsVal = endTs;
  if (startIdx != null && spanSlots > 1) {
    const endSlot = timeslots.find((t) => t.indexInDay === startIdx + spanSlots - 1);
    if (endSlot) endTsVal = endSlot.endTs;
  }
  return `${start} – ${formatTime(endTsVal)}`;
}

export function buildScheduledSessionList(
  rooms: Room[],
  timeslots: Timeslot[],
  sessions: Session[],
  scheduleItems: ScheduleItem[],
  options?: {
    interestFilter?: boolean;
    filterSessionIds?: Set<string>;
  }
): ScheduledSessionEntry[] {
  const roomMap = new Map(rooms.map((r) => [r.id, r]));
  const sessionMap = new Map(sessions.map((s) => [s.id, s]));
  const timeslotByIndex = new Map(timeslots.map((t) => [t.indexInDay, t]));

  const entries: ScheduledSessionEntry[] = [];

  for (const item of scheduleItems) {
    const session = sessionMap.get(item.sessionId);
    const room = roomMap.get(item.roomId);
    const timeslot = timeslotByIndex.get(item.timeslotIndexStart);
    if (!session || !room || !timeslot || timeslot.isBreak) continue;

    if (options?.interestFilter && options.filterSessionIds && !options.filterSessionIds.has(session.id)) {
      continue;
    }

    entries.push({ session, item, room, timeslot });
  }

  entries.sort((a, b) => {
    const timeCmp = a.timeslot.startTs.localeCompare(b.timeslot.startTs);
    if (timeCmp !== 0) return timeCmp;
    return a.room.sortOrder - b.room.sortOrder || a.room.name.localeCompare(b.room.name);
  });

  return entries;
}

export function BoardListView({
  rooms,
  timeslots,
  sessions,
  scheduleItems,
  votedSessionIds,
  bookmarkedSessionIds,
  interestFilter,
  filterSessionIds,
  onSessionClick,
  canBookmark,
  onToggleBookmark,
}: BoardListViewProps) {
  const entries = buildScheduledSessionList(rooms, timeslots, sessions, scheduleItems, {
    interestFilter,
    filterSessionIds,
  });

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="board-list-view space-y-3" role="list" aria-label="Session list">
      {entries.map(({ session, item, room, timeslot }) => {
        const interestCount =
          (session.proposalVoteCount ?? 0) + (session.voteCount ?? 0);
        const voted = votedSessionIds?.has(session.id);
        const bookmarked = bookmarkedSessionIds?.has(session.id);

        return (
          <article
            key={`${session.id}-${item.roomId}-${item.timeslotIndexStart}`}
            role="listitem"
            className="board-list-item card card-hover cursor-pointer relative"
            onClick={() => onSessionClick?.(session.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSessionClick?.(session.id);
              }
            }}
            tabIndex={0}
            aria-label={`${session.title} in ${room.name}`}
          >
            {canBookmark && onToggleBookmark && (
              <SessionQuickBookmark
                bookmarked={!!bookmarked}
                onToggle={() => onToggleBookmark(session.id)}
              />
            )}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className={`flex-1 min-w-0 ${canBookmark ? 'pr-8' : ''}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-500 tabular-nums">
                    {formatDateRange(timeslot.startTs, timeslot.endTs, item.spanSlots, timeslots)}
                  </span>
                  <span className="text-sm text-gray-400">·</span>
                  <span className="text-sm font-medium text-primary-700">{room.name}</span>
                </div>
                <h3 className="font-semibold text-gray-900 mt-1">{session.title}</h3>
                {session.people.length > 0 && (
                  <p className="text-sm text-gray-600 mt-0.5">
                    {session.people
                      .filter((p) => p.role === 'presenter')
                      .map((p) => p.nickname)
                      .join(', ')}
                  </p>
                )}
                {(() => {
                  const f = session.fields;
                  const chips: string[] = [];
                  if (f?.format) chips.push(f.format);
                  if (f?.language) chips.push(f.language);
                  if (f?.level) chips.push(f.level);
                  return chips.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {chips.map((c) => (
                        <span
                          key={c}
                          className="inline-block text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  ) : null;
                })()}
                <SessionStatusTags going={bookmarked} interested={voted} />
              </div>
              <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                {interestCount > 0 && (
                  <span
                    className="flex items-center gap-1 text-xs font-semibold"
                    style={{ color: 'var(--color-brand-gold)' }}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    {interestCount}
                  </span>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
